import { useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  Page,
  PageTitle,
  Table,
  Pagination,
  MobileTable,
  Link,
  Loader,
  ExternalLink
} from '../../../components';
import { useMediaQuery, Button, Box } from '@mui/material';
import { getRecentItems, getDomainUsers, getPerson } from '../../../fetch';
import {
  IRecentItem,
  IDateRange,
  ISortDirection,
  IDropdownResponse,
  IDomainUser,
  IPeopleDetail,
  IAllUser
} from '../../../models';
import { formatDate, formatShortFriendlyDateWithTime } from '../../../helpers';
import { ActivityFilters } from './activity-tracking-filters';
import { Map, PreviewRounded } from '@mui/icons-material';
import { DetailsModal } from './activty-details-modal';
import { InternalUserModal } from '../../admin/manage-admin-users/internal-user-modal';
import { AddEditExternalUser } from '../../admin/manage-admin-users/ExternalUserModal';

export const ActivityTracking = () => {
  const { enqueueSnackbar } = useSnackbar();

  const isMobile = useMediaQuery('(max-width: 1024px)');

  const [recentItems, setRecentItems] = useState<IRecentItem[]>([]);
  const [isLoadingRecentItems, setLoadingRecentItems] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [typeValues, setTypeValues] = useState<IDropdownResponse[]>([]);
  const [inputTypeValue, setInputTypeValue] = useState('');
  const [actionValues, setActionValues] = useState<IDropdownResponse[]>([]);
  const [inputActionValue, setInputActionValue] = useState('');
  const [userValue, setUserValue] = useState<IAllUser | null>(null);
  const [inputUserValue, setInputUserValue] = useState('');
  const [selectedSort, setSelectedSort] = useState<string>('WhenAccessed');
  const [sortDirection, setSortDirection] = useState<{
    WhenAccessed?: ISortDirection;
    RecordType?: ISortDirection;
    HowAccessed?: ISortDirection;
    Username?: ISortDirection;
    Description?: ISortDirection;
    ChangeField?: ISortDirection;
    City?: ISortDirection;
    CountryCode?: ISortDirection;
    InternetServiceProvider?: ISortDirection;
    IpAddress?: ISortDirection;
    Organization?: ISortDirection;
    RegionName?: ISortDirection;
    ZipCode?: ISortDirection;
    IsHosting?: ISortDirection;
    IsMobile?: ISortDirection;
    IsProxy?: ISortDirection;
  }>({ WhenAccessed: 'Desc' });

  const getInitialDateRange = () => {
    const today = new Date();
    const lastMonthAtMidnight = new Date(today);
    lastMonthAtMidnight.setMonth(today.getMonth() - 1);
    lastMonthAtMidnight.setHours(0, 0, 0, 0);
    return {
      startDate: lastMonthAtMidnight,
      endDate: today,
      key: 'selection',
      inputValue: `${formatDate(lastMonthAtMidnight)} - ${formatDate(today)}`
    };
  };

  const initialDates = getInitialDateRange();
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>(initialDates);
  const [searchValue, setSearchValue] = useState<{
    [key: string]: string;
  }>({
    search: '',
    changesSearch: ''
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState(true);
  const [filters, setFilters] = useState<{
    startDate?: string;
    endDate?: string;
    userName?: string;
    search?: string;
    description?: string;
    changesSearch?: string;
    recordTypes?: string[];
    howAccessedTypes?: string[];
    username?: string;
  }>({
    startDate: selectedDateRange?.startDate?.toISOString() || undefined,
    endDate: selectedDateRange?.endDate?.toISOString() || undefined
  });
  const [recentItem, setRecentItem] = useState<IRecentItem | null>(null);
  const [selectedUser, setSelectedUser] = useState<IDomainUser | null>(null);
  const [isSelectRoleModalOpen, setIsSelectRoleModalOpen] = useState(false);
  const [currentExternalUser, setCurrentExternalUser] = useState<IPeopleDetail>(null);
  const [isAddEditExternalUserModalOpen, setIsAddEditExternalUserModalOpen] = useState(false);
  const [isLoadingExternalUser, setIsLoadingExternalUser] = useState(false);
  const [isLoadingInternalUser, setIsLoadingInternalUser] = useState(false);

  const fetchRecentItems = async () => {
    try {
      setLoadingRecentItems(true);
      const res = await getRecentItems({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      });
      setRecordCount(res.totalRecordCount);
      setRecentItems(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading recent items, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingRecentItems(false);
    }
  };

  useEffect(() => {
    fetchRecentItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filters, sortDirection, selectedSort]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleInternalEdit = async (domainUser: IDomainUser) => {
    setSelectedUser(domainUser);
    setIsSelectRoleModalOpen(true);
  };

  const handleExternalEdit = async (id: string, showLoading: boolean) => {
    try {
      if (showLoading) {
        setIsLoadingExternalUser(true);
      }
      const externalUser = await getPerson(id);
      setCurrentExternalUser(externalUser);
      setIsAddEditExternalUserModalOpen(true);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading user, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingExternalUser(false);
    }
  };

  const findInternalUser = async (displayName: string) => {
    try {
      setIsLoadingInternalUser(true);
      const users = await getDomainUsers({
        query: displayName
      });
      const selectedUser = users?.records?.find(
        user => user.displayName === displayName || user.accountName === displayName
      );
      if (selectedUser) {
        handleInternalEdit(selectedUser);
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading user, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingInternalUser(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Activity Date/Time',
        accessor: 'whenAccessed',
        Cell: ({ cell }) => {
          return <>{formatShortFriendlyDateWithTime(cell.row.original.whenAccessed)}</>;
        },
        isServerSorted: selectedSort === 'WhenAccessed',
        isServerSortedDesc: sortDirection.WhenAccessed === 'Desc',
        handleClickColumn: () => handleClickColumn('WhenAccessed')
      },
      {
        Header: 'Activity Type',
        accessor: 'activityType',
        isServerSorted: selectedSort === 'RecordType',
        isServerSortedDesc: sortDirection.RecordType === 'Desc',
        handleClickColumn: () => handleClickColumn('RecordType')
      },
      {
        Header: 'Action',
        accessor: 'howAccessed',
        isServerSorted: selectedSort === 'HowAccessed',
        isServerSortedDesc: sortDirection.HowAccessed === 'Desc',
        handleClickColumn: () => handleClickColumn('HowAccessed')
      },
      {
        Header: 'User',
        accessor: 'username',
        isServerSorted: selectedSort === 'Username',
        isServerSortedDesc: sortDirection.Username === 'Desc',
        handleClickColumn: () => handleClickColumn('Username'),
        Cell: ({ cell }) => {
          const { displayName, username, placeOrderRole, personId } = cell.row.original;
          return (displayName || username) && (placeOrderRole || personId) ? (
            <Button
              onClick={() => {
                if (placeOrderRole) {
                  findInternalUser(displayName || username);
                } else if (personId) {
                  handleExternalEdit(personId, true);
                }
              }}
              color="primary"
              sx={{ textTransform: 'none', textAlign: 'left', minWidth: 'initial', padding: 0 }}
            >
              {displayName || username}
            </Button>
          ) : (
            <>{displayName || username}</>
          );
        }
      },
      {
        Header: 'Record',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description'),
        Cell: ({ cell }) => {
          const { description, activityType, recordId } = cell?.row?.original as IRecentItem;
          return description && recordId && activityType === 'Place Order' ? (
            <>
              <Link to={`/production/orders/order-management/${recordId}`}>{description}</Link>
            </>
          ) : (
            <>{description}</>
          );
        }
      },
      {
        Header: 'Region',
        accessor: 'regionName',
        isServerSorted: selectedSort === 'RegionName',
        isServerSortedDesc: sortDirection.RegionName === 'Desc',
        handleClickColumn: () => handleClickColumn('RegionName')
      },
      {
        Header: 'City',
        accessor: 'city',
        isServerSorted: selectedSort === 'City',
        isServerSortedDesc: sortDirection.City === 'Desc',
        handleClickColumn: () => handleClickColumn('City')
      },
      {
        Header: 'Location',
        accessor: 'location',
        Cell: ({ cell }) => {
          const { latitude, longitude } = cell?.row?.original as IRecentItem;
          return latitude && longitude ? (
            <ExternalLink
              type="secondary"
              target="_blank"
              to={`https://maps.google.com/?q=${latitude},${longitude}`}
              title="View Map on Google Maps"
              className="print--none"
            >
              <Box component="span" display="flex" alignItems="center">
                <Map sx={{ marginRight: '5px' }} /> Map
              </Box>
            </ExternalLink>
          ) : (
            <></>
          );
        }
      },
      {
        Header: 'Details',
        accessor: 'changes',
        sort: false,
        Cell: ({ cell }) => {
          return (
            <Button
              color="secondary"
              onClick={() => setRecentItem(cell.row?.original)}
              startIcon={<PreviewRounded />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              View
            </Button>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentItems, isMobile, setRecentItem, sortDirection, selectedSort]);

  return (
    <>
      <Page title="Activity Tracking">
        <PageTitle title={'Activity Tracking'} />
        {(isLoadingExternalUser || isLoadingInternalUser) && (
          <Loader position="centered" type="fullscreen" />
        )}
        <ActivityFilters
          isLoading={isLoadingRecentItems}
          hasAppliedFilters={hasAppliedFilters}
          setHasAppliedFilters={setHasAppliedFilters}
          applyFilters={(clearFilters?: boolean, clearKey?: string) => {
            if (clearKey) {
              const newFilters = {
                ...filters,
                changesSearch: clearKey === 'changesSearch' ? '' : filters.changesSearch,
                search: clearKey === 'search' ? '' : filters.search,
                recordTypes:
                  clearKey === 'recordTypes' || filters?.recordTypes?.length === 0
                    ? undefined
                    : filters.recordTypes,
                howAccessedTypes:
                  clearKey === 'howAccessedTypes' || filters?.howAccessedTypes?.length === 0
                    ? undefined
                    : filters.howAccessedTypes
              };
              const hasFilters = Object.values(newFilters).filter(Boolean).length > 0;
              setHasAppliedFilters(hasFilters);
              return setFilters(newFilters);
            }
            if (clearFilters) {
              setSelectedDateRange(undefined);
              return setFilters({
                startDate: undefined,
                endDate: undefined,
                recordTypes: undefined,
                howAccessedTypes: undefined,
                search: '',
                changesSearch: '',
                username: ''
              });
            }
            setPage(0);
            setFilters({
              ...filters,
              startDate: selectedDateRange?.startDate?.toISOString() || undefined,
              endDate: selectedDateRange?.endDate?.toISOString() || undefined,
              recordTypes: typeValues?.map(t => t.value) ?? undefined,
              howAccessedTypes: actionValues?.map(t => t.value) ?? undefined,
              search: searchValue?.search ?? '',
              changesSearch: searchValue?.changesSearch ?? '',
              username: userValue?.userName ?? ''
            });
          }}
          handleGlobalSearch={(val: { [key: string]: string }) => {
            setPage(0);
            setHasAppliedFilters(true);
            setFilters({
              ...filters,
              search: val?.search ?? ''
            });
          }}
          handleDataSearch={(val: { [key: string]: string }) => {
            setPage(0);
            setHasAppliedFilters(true);
            setFilters({
              ...filters,
              changesSearch: val?.changesSearch ?? ''
            });
          }}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          typeValues={typeValues}
          setTypeValues={setTypeValues}
          inputTypeValue={inputTypeValue}
          setInputTypeValue={setInputTypeValue}
          actionValues={actionValues}
          setActionValues={setActionValues}
          inputActionValue={inputActionValue}
          setInputActionValue={setInputActionValue}
          userValue={userValue}
          inputUserValue={inputUserValue}
          setInputUserValue={setInputUserValue}
          setUserValue={setUserValue}
        />
        <Table
          columns={columns}
          data={recentItems}
          isLoading={isLoadingRecentItems}
          hidePagination
          mobileBreakPoint={1024}
          mobileProps={{
            useFirstCell: true,
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
        {!isLoadingRecentItems && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
      <DetailsModal recentItem={recentItem} setRecentItem={setRecentItem} />
      {selectedUser && (
        <InternalUserModal
          currentUser={selectedUser}
          open={isSelectRoleModalOpen}
          onClose={(shouldReload: boolean) => {
            setSelectedUser(null);
            setIsSelectRoleModalOpen(false);
            if (shouldReload) {
              fetchRecentItems();
            }
          }}
        />
      )}
      <AddEditExternalUser
        open={isAddEditExternalUserModalOpen}
        onClose={(hasSubmitted: boolean) => {
          setIsAddEditExternalUserModalOpen(false);
          setCurrentExternalUser(null);

          if (hasSubmitted) {
            fetchRecentItems();
          }
        }}
        handleExternalEdit={handleExternalEdit}
        currentExternalUser={currentExternalUser}
      />
    </>
  );
};
